
(() => {
  const selector = '[data-hk-pdp-sticky-nav]';

  function findTextTarget(text) {
    if (!text) return null;
    const search = text.trim().toLowerCase();
    const nodes = document.querySelectorAll(
      'h1,h2,h3,h4,h5,h6,.h1,.h2,.h3,[class*="title" i],[class*="heading" i]'
    );

    for (const node of nodes) {
      const value = (node.textContent || '').trim().toLowerCase();
      if (value === search || (value.length < 70 && value.includes(search))) {
        return node.closest('.shopify-section') || node;
      }
    }
    return null;
  }

  function resolveTarget(button) {
    const target = (button.dataset.target || '').trim();
    const label = (button.dataset.label || '').trim();

    if (target === '__overview__') {
      const productInfo = document.querySelector('product-info');
      return productInfo?.closest('.shopify-section') || productInfo;
    }

    if (target === '__specs__') {
      return document.querySelector('.hkh-specs')
        || findTextTarget('Technical Specifications')
        || findTextTarget('Specifications');
    }

    if (target === '__faqs__') {
      return document.querySelector('.h2p-faq-section')
        || findTextTarget('Frequently Asked Questions')
        || findTextTarget('FAQ');
    }

    if (target) {
      const byId = document.getElementById(target);
      if (byId) return byId;

      try {
        const byClass = document.querySelector(`.${CSS.escape(target)}`);
        if (byClass) return byClass;
      } catch (_) {}

      const byText = findTextTarget(target);
      if (byText) return byText;
    }

    return findTextTarget(label);
  }

  function setActive(root, button) {
    root.querySelectorAll('[data-hk-pdp-tab]').forEach((tab) => {
      const active = tab === button;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  function updateStickyTop(root) {
    const host = root.closest('.shopify-section');
    if (!host) return;

    const header = document.querySelector(
      'sticky-header, .header-wrapper, #shopify-section-header, header'
    );

    let top = 0;
    if (header) {
      const rect = header.getBoundingClientRect();
      top = Math.max(0, rect.bottom);
    }
    host.style.top = `${top}px`;
  }

  function readVariantMap(root) {
    const script = root.querySelector('[data-hk-sticky-variants]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  function initRoot(root) {
    if (root.dataset.hkStickyInitialized === 'true') return;
    root.dataset.hkStickyInitialized = 'true';

    const host = root.closest('.shopify-section');
    if (host) host.classList.add('hk-pdp-sticky-host');

    // Cart drawer lifecycle -----------------------------------------------------
    // Keep the sticky bar's layout slot stable and only animate its opacity.
    // On close we reveal the bar *during* the drawer transition, matching the
    // smooth reference behaviour, while a short RAF guard prevents Concept's
    // focus/body-unlock routine from visibly jumping the page back to the buy box.
    let cartIsOpen = false;
    let cartIsClosing = false;
    let cartSavedScrollY = null;
    let cartVisibilityFrame = 0;
    let cartCloseGuardFrame = 0;
    let cartClosingUntil = 0;

    if (root.classList.contains('hk-pdp-sticky-nav--h2pro')) {
      document.documentElement.classList.add('hk-h2pro-sticky-page');
    }

    const cartOpenSelectors = [
      'cart-drawer[open]',
      'cart-drawer.active',
      'cart-drawer.is-open',
      'cart-drawer[aria-hidden="false"]',
      'mini-cart[open]',
      'mini-cart.active',
      'mini-cart.is-open',
      'mini-cart[aria-hidden="false"]',
      '#CartDrawer[open]',
      '#CartDrawer.active',
      '#CartDrawer.is-open',
      '#CartDrawer[aria-hidden="false"]',
      '#MiniCart[open]',
      '#MiniCart.active',
      '#MiniCart.is-open',
      '#MiniCart[aria-hidden="false"]',
      '.cart-drawer[open]',
      '.cart-drawer.active',
      '.cart-drawer.is-open',
      '.cart-drawer[aria-hidden="false"]',
      '.mini-cart[open]',
      '.mini-cart.active',
      '.mini-cart.is-open',
      '.mini-cart[aria-hidden="false"]',
      '[data-cart-drawer][open]',
      '[data-cart-drawer].active',
      '[data-cart-drawer].is-open',
      '[data-cart-drawer][aria-hidden="false"]',
      'side-drawer[open][id*="cart" i]',
      'side-drawer[open][class*="cart" i]',
      'side-drawer[open][id*="mini" i]',
      'side-drawer[open][class*="mini" i]',
      '[role="dialog"][open][id*="cart" i]',
      '[role="dialog"][aria-modal="true"][id*="cart" i]',
      '[role="dialog"][aria-hidden="false"][id*="cart" i]'
    ];

    const isVisibleElement = (el) => {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        Number.parseFloat(style.opacity || '1') < 0.05
      ) return false;

      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;

      // Concept often keeps the drawer mounted and translates it off-canvas.
      return (
        rect.right > 1 &&
        rect.left < (window.innerWidth - 1) &&
        rect.bottom > 1 &&
        rect.top < (window.innerHeight - 1)
      );
    };

    const isCartDrawerOpen = () => cartOpenSelectors.some((query) => {
      try {
        return Array.from(document.querySelectorAll(query)).some(isVisibleElement);
      } catch (_) {
        return false;
      }
    });

    const cancelCartCloseGuard = () => {
      if (cartCloseGuardFrame) cancelAnimationFrame(cartCloseGuardFrame);
      cartCloseGuardFrame = 0;
    };

    const beginCartOpen = () => {
      if (!host) return;
      cancelCartCloseGuard();
      if (!cartIsOpen && cartSavedScrollY == null) cartSavedScrollY = window.scrollY;
      cartIsOpen = true;
      cartIsClosing = false;
      cartClosingUntil = 0;
      host.classList.remove('is-cart-drawer-closing');
      host.classList.add('is-cart-drawer-open');
    };

    const finishCartClose = () => {
      if (!host) return;
      cancelCartCloseGuard();
      cartIsOpen = false;
      cartIsClosing = false;
      cartClosingUntil = 0;
      host.classList.remove('is-cart-drawer-open', 'is-cart-drawer-closing');
      cartSavedScrollY = null;
    };

    const beginCartClose = () => {
      if (!host) return;
      if (cartIsClosing) return;

      const targetY = cartSavedScrollY == null ? window.scrollY : cartSavedScrollY;
      cartIsOpen = false;
      cartIsClosing = true;
      cartClosingUntil = performance.now() + 720;

      // Reveal immediately while the drawer itself is sliding away. Do not wait
      // for display/aria attributes to settle, otherwise the bar "pops" back in.
      host.classList.remove('is-cart-drawer-open');
      host.classList.add('is-cart-drawer-closing');

      cancelCartCloseGuard();
      const guardPosition = () => {
        if (!cartIsClosing) return;

        // Keep the viewport pinned through the theme's focus restoration/body
        // unlock before the browser paints, avoiding the old visible flash/jump.
        if (Math.abs(window.scrollY - targetY) > 2) {
          window.scrollTo({ top: targetY, left: window.scrollX, behavior: 'auto' });
        }

        if (performance.now() < cartClosingUntil) {
          cartCloseGuardFrame = requestAnimationFrame(guardPosition);
          return;
        }

        cartCloseGuardFrame = 0;
        cartIsClosing = false;
        host.classList.remove('is-cart-drawer-closing');

        // One final real-state check. If the theme is unusually slow and the cart
        // is still visibly open, hide the bar again; otherwise release the snapshot.
        if (isCartDrawerOpen()) beginCartOpen();
        else cartSavedScrollY = null;
      };

      cartCloseGuardFrame = requestAnimationFrame(guardPosition);
    };

    const syncCartDrawerVisibility = () => {
      if (!host) return;
      if (cartVisibilityFrame) cancelAnimationFrame(cartVisibilityFrame);

      cartVisibilityFrame = requestAnimationFrame(() => {
        const detectedOpen = isCartDrawerOpen();

        // During the CSS close transition the drawer may still technically be
        // "visible". Ignore that stale state so the sticky bar can fade back in
        // at the same time as the cart is sliding away.
        if (cartIsClosing && performance.now() < cartClosingUntil) return;

        if (detectedOpen) {
          beginCartOpen();
        } else if (cartIsOpen) {
          beginCartClose();
        } else if (!cartIsClosing) {
          host.classList.remove('is-cart-drawer-open', 'is-cart-drawer-closing');
        }
      });
    };

    syncCartDrawerVisibility();

    const cartObserver = new MutationObserver(syncCartDrawerVisibility);
    cartObserver.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open', 'class', 'aria-hidden', 'aria-modal', 'style']
    });

    const forceCartState = (open) => {
      if (open) beginCartOpen();
      else beginCartClose();

      // Reconcile after the drawer transition instead of fighting the transition
      // every frame with contradictory open/close states.
      window.setTimeout(syncCartDrawerVisibility, 760);
      window.setTimeout(syncCartDrawerVisibility, 980);
    };

    ['cart:open', 'cart:opened', 'cart-drawer:open', 'cart-drawer:opened']
      .forEach((eventName) => document.addEventListener(eventName, () => forceCartState(true)));

    ['cart:close', 'cart:closed', 'cart-drawer:close', 'cart-drawer:closed']
      .forEach((eventName) => document.addEventListener(eventName, () => forceCartState(false)));

    // Generic drawer events can belong to Specs/Help, so only re-check actual
    // cart visibility instead of forcing state from these events.
    ['drawer:open', 'drawer:opened', 'drawer:close', 'drawer:closed']
      .forEach((eventName) => document.addEventListener(eventName, () => {
        window.setTimeout(syncCartDrawerVisibility, 60);
        window.setTimeout(syncCartDrawerVisibility, 760);
      }));

    // Start the reveal at the user's close gesture rather than waiting for the
    // drawer DOM attributes to update at the end of its animation.
    document.addEventListener('click', (event) => {
      if (!cartIsOpen || !(event.target instanceof Element)) return;
      const cartContext = event.target.closest(
        'cart-drawer,mini-cart,#CartDrawer,#MiniCart,.cart-drawer,.mini-cart,[data-cart-drawer],side-drawer[id*="cart" i],side-drawer[class*="cart" i]'
      );
      if (!cartContext) return;

      const control = event.target.closest('button,a,[role="button"],[data-close],[data-drawer-close]');
      if (!control) return;
      const hint = `${control.getAttribute('aria-label') || ''} ${control.getAttribute('title') || ''} ${control.className || ''} ${control.id || ''} ${control.getAttribute('data-close') || ''} ${control.getAttribute('data-drawer-close') || ''}`.toLowerCase();
      if (hint.includes('close') || hint.includes('dismiss')) beginCartClose();
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && cartIsOpen) beginCartClose();
    }, true);

    document.addEventListener('transitionend', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const hint = `${target.id || ''} ${target.className || ''} ${target.tagName || ''}`.toLowerCase();
      if (hint.includes('cart') || hint.includes('mini-cart') || hint.includes('drawer')) {
        window.setTimeout(syncCartDrawerVisibility, 0);
      }
    }, true);

    window.addEventListener('focus', syncCartDrawerVisibility, { passive: true });

    const tabs = [...root.querySelectorAll('[data-hk-pdp-tab]')];
    const targetPairs = tabs
      .map((tab) => ({ tab, target: resolveTarget(tab) }))
      .filter((item) => item.target);

    let manualActiveTab = null;
    let manualActiveUntil = 0;
    let scrollSettleTimer = 0;
    let ticking = false;

    const pairDocumentTop = (pair) =>
      pair.target.getBoundingClientRect().top + window.scrollY;

    const updateActiveFromScroll = (force = false) => {
      if (!targetPairs.length) return;
      if (!force && manualActiveTab && performance.now() < manualActiveUntil) return;

      updateStickyTop(root);
      const rootHeight = root.getBoundingClientRect().height || 0;
      const hostTop = parseFloat(host?.style.top || '0') || 0;
      // Slight tolerance after the sticky bar is important for sections whose
      // visible heading begins a few pixels below the actual Shopify section node.
      const markerY = window.scrollY + hostTop + rootHeight + Math.max(40, rootHeight * 0.65);

      let current = targetPairs[0];
      let currentTop = -Infinity;

      for (const pair of targetPairs) {
        const top = pairDocumentTop(pair);
        if (top <= markerY && top >= currentTop) {
          current = pair;
          currentTop = top;
        }
      }

      if (current?.tab) setActive(root, current.tab);
    };

    const finishManualNavigation = () => {
      if (!manualActiveTab) return;
      // Keep the clicked state through the very end of smooth scrolling. A short
      // post-settle delay prevents the scroll spy from snapping back to Overview.
      setActive(root, manualActiveTab);
      const clickedTab = manualActiveTab;
      manualActiveUntil = performance.now() + 120;
      window.setTimeout(() => {
        if (manualActiveTab !== clickedTab) return;
        manualActiveTab = null;
        manualActiveUntil = 0;
        updateActiveFromScroll(true);
      }, 140);
    };

    tabs.forEach((tab, index) => {
      if (index === 0) setActive(root, tab);

      tab.addEventListener('click', () => {
        const target = resolveTarget(tab);
        setActive(root, tab);
        if (!target) return;

        manualActiveTab = tab;
        manualActiveUntil = performance.now() + 1800;

        const rootHeight = root.getBoundingClientRect().height;
        const hostTop = parseFloat(host?.style.top || '0') || 0;
        const y =
          target.getBoundingClientRect().top
          + window.scrollY
          - rootHeight
          - hostTop
          - 12;

        window.scrollTo({
          top: Math.max(0, y),
          behavior: 'smooth'
        });

        if (scrollSettleTimer) window.clearTimeout(scrollSettleTimer);
        scrollSettleTimer = window.setTimeout(finishManualNavigation, 900);
      });
    });

    const onScroll = () => {
      updateStickyTop(root);

      if (manualActiveTab) {
        if (scrollSettleTimer) window.clearTimeout(scrollSettleTimer);
        scrollSettleTimer = window.setTimeout(finishManualNavigation, 170);
      }

      if (ticking || !targetPairs.length) return;
      ticking = true;

      requestAnimationFrame(() => {
        updateActiveFromScroll(false);
        ticking = false;
      });
    };

    // Native scrollend gives perfect timing in modern browsers; the debounce in
    // onScroll above is the fallback for Safari/older Chromium.
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', finishManualNavigation, { passive: true });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      updateStickyTop(root);
      updateActiveFromScroll(true);
    }, { passive: true });
    onScroll();

    const variantMap = readVariantMap(root);
    const hiddenId = root.querySelector('[data-hk-sticky-variant-id]');
    const submit = root.querySelector('[data-hk-sticky-submit]');
    const submitText = root.querySelector('[data-hk-sticky-submit-text]');
    const price = root.querySelector('[data-hk-sticky-product-price]');
    const image = root.querySelector('[data-hk-sticky-product-image]');
    const stickyForm = submit?.closest('form');
    const defaultSubmitLabel = submit?.dataset.addLabel || submitText?.textContent?.trim() || 'Add to cart';
    let submitStateTimer = null;

    function setStickySubmitState(state, label) {
      if (!submit) return;
      if (submitStateTimer) {
        window.clearTimeout(submitStateTimer);
        submitStateTimer = null;
      }
      submit.classList.remove('is-loading', 'is-success', 'is-error');
      if (state) submit.classList.add(`is-${state}`);
      if (submitText && label) submitText.textContent = label;
    }

    function restoreStickySubmit(delay = 0) {
      const restore = () => {
        if (!submit || submit.disabled) return;
        setStickySubmitState('', defaultSubmitLabel);
      };
      if (delay > 0) submitStateTimer = window.setTimeout(restore, delay);
      else restore();
    }

    function findMainProductForm() {
      const forms = Array.from(document.querySelectorAll(
        'product-info form[is="product-form"], product-info form[data-type="add-to-cart-form"]'
      )).filter((form) => !form.closest('[data-hk-pdp-sticky-nav]'));
      if (!forms.length) return null;

      const currentVariantId = String(hiddenId?.value || '');
      return forms.find((form) => String(form.querySelector('[name="id"]')?.value || '') === currentVariantId)
        || forms.find((form) => {
          const button = form.querySelector('[type="submit"][name="add"], .product-form__submit[type="submit"]');
          return button && !button.disabled;
        })
        || forms[0];
    }

    function triggerNativeProductAdd(event) {
      if (!submit || submit.disabled) return;
      event?.preventDefault?.();
      event?.stopPropagation?.();

      const mainForm = findMainProductForm();
      const mainSubmit = mainForm?.querySelector('[type="submit"][name="add"], .product-form__submit[type="submit"], [type="submit"]');

      setStickySubmitState('loading', 'Adding…');

      // Save the exact viewport position in the same variable used by the
      // cart lifecycle above. V19.5 accidentally referenced two removed variables
      // here, which threw a ReferenceError before the real product button could run
      // and left the sticky CTA permanently on "Adding…".
      if (cartSavedScrollY == null) cartSavedScrollY = window.scrollY;
      try { submit.focus({ preventScroll: true }); } catch (_) {}

      try {
        if (mainForm && mainSubmit && !mainSubmit.disabled) {
          // IMPORTANT: click the *real* PDP submit button instead of only dispatching
          // a form submit. HIKNIGHT's accessory upsell/cart chain is attached to the
          // native Add-to-cart click path, so this mirrors a real customer click and
          // preserves the existing accessories -> cart drawer workflow.
          if (typeof mainSubmit.click === 'function') mainSubmit.click();
          else if (typeof mainForm.requestSubmit === 'function') mainForm.requestSubmit(mainSubmit);
          else mainForm.submit();
          return;
        }

        // Defensive fallback for templates where product-info is not present.
        if (stickyForm) {
          if (typeof stickyForm.requestSubmit === 'function') stickyForm.requestSubmit(submit);
          else stickyForm.submit();
        }
      } catch (error) {
        console.error('[HIKNIGHT sticky add]', error);
        setStickySubmitState('error', 'Try again');
        restoreStickySubmit(1200);
      }
    }

    // Capture the click before the sticky form submits itself. This prevents the
    // detached sticky form from bypassing the PDP's normal accessory/cart workflow.
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-hk-sticky-submit]');
      if (!button || !root.contains(button)) return;
      triggerNativeProductAdd(event);
    }, true);

    document.addEventListener('ajaxProduct:added', () => {
      if (!submit?.classList.contains('is-loading')) return;
      setStickySubmitState('success', 'Added ✓');
      restoreStickySubmit(950);
    });

    document.addEventListener('ajaxProduct:error', () => {
      if (!submit?.classList.contains('is-loading')) return;
      setStickySubmitState('error', 'Try again');
      restoreStickySubmit(1300);
    });

    function syncVariant(id, fallbackVariant) {
      if (!id) return;

      const key = String(id);
      const data = variantMap[key] || fallbackVariant || {};

      if (hiddenId) hiddenId.value = key;

      const available =
        typeof data.available === 'boolean'
          ? data.available
          : true;

      if (submit) submit.disabled = !available;

      if (submitText) {
        submitText.textContent = available
          ? (submit?.dataset.addLabel || 'Add to cart')
          : (submit?.dataset.soldLabel || 'Sold out');
      }

      if (price && data.price) {
        price.textContent = data.price;
      }

      const imageUrl =
        data.image
        || data.featured_media?.preview_image?.src
        || data.featured_image?.src
        || null;

      if (image && imageUrl) {
        image.src = imageUrl;
      }
    }

    function handleVariantEvent(event) {
      const variant =
        event?.data?.variant
        || event?.detail?.variant
        || event?.detail
        || null;

      if (variant?.id) syncVariant(variant.id, variant);
    }

    if (
      window.theme?.pubsub?.subscribe
      && window.theme?.pubsub?.PUB_SUB_EVENTS?.variantChange
    ) {
      window.theme.pubsub.subscribe(
        window.theme.pubsub.PUB_SUB_EVENTS.variantChange,
        handleVariantEvent
      );
    }

    document.addEventListener('variant:change', handleVariantEvent);
    document.addEventListener('variant-change', handleVariantEvent);

    // Last-resort visual reset; real success/error events above normally fire first.
    submit?.addEventListener('click', () => {
      window.setTimeout(() => {
        if (submit?.classList.contains('is-loading')) restoreStickySubmit();
      }, 6500);
    });
  }

  function initAll() {
    document.querySelectorAll(selector).forEach(initRoot);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  initAll();
})();
